/**
 * CV Matching Results Component
 * Displays top 10 CVs matching ToR requirements with explanations
 */

import React from 'react';
import {
    Box,
    Paper,
    Typography,
    Grid,
    Chip,
    Stack,
    Card,
    CardContent,
    LinearProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Button
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    Star as StarIcon,
    Article as ArticleIcon,
    Chat as ChatIcon,
    WorkHistory as WorkHistoryIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import DOMPurify from 'dompurify';
import { CVMatch, AdditionalMatch } from '../services/cvMatching';
import { getFilteredEvidenceCategories } from '../utils/cvMatchCategories';

interface CVMatchingResultsProps {
    matches: CVMatch[];
    additionalMatches?: AdditionalMatch[]; // CVs that passed verification but didn't get LLM analysis
    searchCriteria?: {
        geographic?: string;
        required_methods?: string[];
        languages?: string[];
    };
    selectedCategories?: string[]; // NEW: Categories that were searched
}

const CVMatchingResults: React.FC<CVMatchingResultsProps> = ({ matches, additionalMatches, searchCriteria, selectedCategories }) => {
    // Get qualitative label for similarity score - user-friendly language
    const getMatchLabel = (percentage: number): { label: string; color: string } => {
        if (percentage >= 90) return { label: 'Very Close Fit', color: 'success.main' };
        if (percentage >= 80) return { label: 'Close Fit', color: 'primary.main' };
        if (percentage >= 70) return { label: 'Good Fit', color: 'info.main' };
        if (percentage >= 60) return { label: 'Partial Fit', color: 'warning.main' };
        return { label: 'Some Overlap', color: 'text.secondary' };
    };
    
    // Detect if evidence text appears to be from education section
    // Returns { isEducation: boolean, confidence: 'high' | 'medium' | 'low' }
    const detectEducationSource = (text: string): { isEducation: boolean; confidence: 'high' | 'medium' | 'low' } => {
        const textLower = text.toLowerCase();
        
        // High confidence: Degree names + institution patterns
        const degreePatterns = /\b(bsc|ba|beng|llb|msc|ma|mba|mphil|mpa|mph|phd|dphil|doctorate|bachelor|master|diploma|certificate)\b/i;
        const institutionPatterns = /\b(university|college|institute|school of|faculty of|department of)\b/i;
        const yearPattern = /\b(19[5-9]\d|20[0-2]\d)\b/; // Years 1950-2029
        
        const hasDegree = degreePatterns.test(text);
        const hasInstitution = institutionPatterns.test(text);
        const hasYear = yearPattern.test(text);
        
        // High confidence: Has degree AND (institution OR year in education context)
        if (hasDegree && (hasInstitution || hasYear)) {
            return { isEducation: true, confidence: 'high' };
        }
        
        // Medium confidence: Has institution name without clear project context
        if (hasInstitution && !textLower.includes('project') && !textLower.includes('evaluation') && !textLower.includes('consultant')) {
            return { isEducation: true, confidence: 'medium' };
        }
        
        return { isEducation: false, confidence: 'low' };
    };
    
    // Render evidence item with consistent formatting: green criterion, white text
    // Now includes section source badge for education-related evidence
    // Skip Education chip when already rendering in the Education section (redundant)
    const renderEvidenceItem = (text: string, idx: number, sectionTitle?: string) => {
        const educationCheck = detectEducationSource(text);
        const showEducationChip = educationCheck.isEducation && sectionTitle !== 'Education';
        
        // Check if text has "Criterion: detail" format
        const colonIndex = text.indexOf(':');
        if (colonIndex > 0 && colonIndex < 50) {
            const criterion = text.substring(0, colonIndex).trim();
            const detail = text.substring(colonIndex + 1).trim();
            return (
                <Box component="li" key={idx} sx={{ mb: 0.5, display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <Typography component="span" variant="body2" sx={{ color: 'success.main', fontWeight: 600 }}>
                        {criterion}:
                    </Typography>
                    <Typography component="span" variant="body2" sx={{ color: 'text.primary', ml: 0.5, flex: 1 }}>
                        {detail}
                    </Typography>
                    {showEducationChip && (
                        <Chip 
                            label="Education" 
                            size="small" 
                            sx={{ 
                                ml: 1, 
                                height: 18, 
                                fontSize: '0.65rem',
                                bgcolor: educationCheck.confidence === 'high' ? 'warning.main' : 'warning.light',
                                color: 'warning.contrastText'
                            }} 
                        />
                    )}
                </Box>
            );
        }
        // No colon format - render as plain white text with optional badge
        return (
            <Box component="li" key={idx} sx={{ mb: 0.5, display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <Typography component="span" variant="body2" sx={{ color: 'text.primary', flex: 1 }}>
                    {text}
                </Typography>
                {showEducationChip && (
                    <Chip 
                        label="Education" 
                        size="small" 
                        sx={{ 
                            ml: 1, 
                            height: 18, 
                            fontSize: '0.65rem',
                            bgcolor: educationCheck.confidence === 'high' ? 'warning.main' : 'warning.light',
                            color: 'warning.contrastText'
                        }} 
                    />
                )}
            </Box>
        );
    };
    
    // Extract country name from folder path (e.g., "Tanzania/CV-file.pdf" -> "Tanzania")
    const extractCountry = (folderName: string): string => {
        if (!folderName) return '';
        // Split by / and take the first part (country name)
        const parts = folderName.split('/');
        return parts[0] || folderName;
    };
    
    // Handle viewing CV in new tab
    const handleViewDocument = (match: CVMatch) => {
        // Build full path: folder_name/filename
        let documentPath = '';
        
        console.log('🔍 View CV Debug:', {
            filename: match.filename,
            folder_name: (match as any).folder_name,
            source_location: (match as any).source_location,
            associate_name: match.associate_name
        });
        
        if ((match as any).source_location) {
            // Use source_location if available (most reliable)
            documentPath = (match as any).source_location;
            console.log('✅ Using source_location:', documentPath);
        } else if (match.filename) {
            // Check if filename already contains folder path (e.g., "International/file.pdf")
            if (match.filename.includes('/') || match.filename.includes('\\')) {
                // Filename already has path
                documentPath = match.filename;
                console.log('✅ Using filename with path:', documentPath);
            } else if ((match as any).folder_name) {
                // Construct path from folder + filename
                documentPath = `${(match as any).folder_name}/${match.filename}`;
                console.log('✅ Constructed from folder + filename:', documentPath);
            } else {
                // Just filename
                documentPath = match.filename;
                console.log('✅ Using just filename:', documentPath);
            }
        } else if (match.associate_name) {
            documentPath = match.associate_name;
            console.log('✅ Using associate_name:', documentPath);
        } else {
            documentPath = String(match.cv_id);
            console.log('✅ Using cv_id:', documentPath);
        }
        
        if (!documentPath) {
            console.error("❌ Could not determine document path for view:", match);
            return;
        }
        
        // Clean the path for URL
        const cleanPath = documentPath.replace(/\\/g, '/');
        const encodedPath = encodeURIComponent(cleanPath);
        const viewerUrl = `/document/cv/${encodedPath}`;
        
        console.log('🌐 Final URL:', `#${viewerUrl}`);
        
        window.open(`#${viewerUrl}`, '_blank');
    };
    
    // Handle chatting with CV
    const handleChatWithDocument = (match: CVMatch) => {
        // Build full path: folder_name/filename
        let docIdentifier = '';
        
        if ((match as any).source_location) {
            // Use source_location if available (most reliable)
            docIdentifier = (match as any).source_location;
        } else if (match.filename) {
            // Check if filename already contains folder path (e.g., "International/file.pdf")
            if (match.filename.includes('/') || match.filename.includes('\\')) {
                // Filename already has path
                docIdentifier = match.filename;
            } else if ((match as any).folder_name) {
                // Construct path from folder + filename
                docIdentifier = `${(match as any).folder_name}/${match.filename}`;
            } else {
                // Just filename
                docIdentifier = match.filename;
            }
        } else if (match.associate_name) {
            docIdentifier = match.associate_name;
        } else {
            docIdentifier = String(match.cv_id);
        }
        
        if (!docIdentifier) {
            console.error("Could not determine document identifier for chat:", match);
            return;
        }
        
        // Ensure .pdf extension if it's a simple name without extension
        if (typeof docIdentifier === 'string' && !docIdentifier.toLowerCase().endsWith('.pdf') && 
            !docIdentifier.includes('\\') && !docIdentifier.includes('/')) {
            docIdentifier += ".pdf";
        }
        
        const encodedIdentifier = encodeURIComponent(docIdentifier);
        window.open(`#/chat/cv/${encodedIdentifier}`, '_blank');
    };
    
    return (
        <Box>
            
            {/* CV Matches */}
            <Typography variant="h6" gutterBottom sx={{ mb: 1 }}>
                Top {matches.length} Matching CVs
            </Typography>
            
            {/* Similarity explanation for non-technical users */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                Results are ranked by how closely each associate's background matches your selected criteria. 
                The percentage shows overall similarity - associates with higher scores have more overlap 
                in the specific skills, sectors, and experience you selected.
            </Typography>
            
            {matches.length === 0 && (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                        No matching CVs found. Try broadening your requirements.
                    </Typography>
                </Paper>
            )}
            
            <Grid container spacing={2}>
                {matches.map((match, index) => (
                    <Grid item xs={12} key={match.cv_id}>
                        <Card elevation={3} sx={{ 
                            border: '2px solid',
                            borderColor: index < 3 ? 'primary.main' : 'divider',
                            bgcolor: index < 3 ? 'primary.50' : 'background.paper'
                        }}>
                            <CardContent>
                                {/* Header */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                            {index < 3 && <StarIcon color="primary" />}
                                            <Typography variant="h6">
                                                #{index + 1}: {match.associate_name || match.filename}
                                            </Typography>
                                            {(match as any).has_worked_with_us && (
                                                <Chip 
                                                    icon={<WorkHistoryIcon />}
                                                    label={`Worked with us (${(match as any).allocation_count})`}
                                                    size="small"
                                                    color="success"
                                                    variant="outlined"
                                                    sx={{ ml: 1 }}
                                                />
                                            )}
                                        </Stack>
                                        <Typography variant="caption" color="text.secondary">
                                            {extractCountry(match.folder_name)}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'right', minWidth: 120 }}>
                                        {(() => {
                                            const percentage = (match as any).match_percentage || Math.round(match.similarity_score * 100);
                                            const { label, color } = getMatchLabel(percentage);
                                            return (
                                                <>
                                                    <Typography variant="h6" color={color}>
                                                        {percentage}%
                                                    </Typography>
                                                    <Typography variant="caption" color={color} sx={{ fontWeight: 'bold' }}>
                                                        {label}
                                                    </Typography>
                                                    <LinearProgress 
                                                        variant="determinate" 
                                                        value={percentage}
                                                        sx={{ width: 100, mt: 0.5 }}
                                                        color={percentage >= 80 ? 'primary' : percentage >= 60 ? 'warning' : 'inherit'}
                                                    />
                                                </>
                                            );
                                        })()}
                                        {(match as any).match_breakdown && (
                                            <Box sx={{ mt: 1, fontSize: '0.65rem', lineHeight: 1.3 }}>
                                                <Box>✓ Required: {(match as any).match_breakdown.required_met}</Box>
                                                <Box>◐ Preferred: {(match as any).match_breakdown.preferred_met}</Box>
                                                <Box>+ Bonus: {(match as any).match_breakdown.bonus_met}</Box>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                                
                                {/* Associate Summary */}
                                {(match as any).associate_summary && (
                                    <Typography variant="body2" paragraph sx={{ fontWeight: 500, color: 'text.primary', mb: 2, p: 1.5, bgcolor: 'rgba(25, 118, 210, 0.08)', borderRadius: 1 }}>
                                        {(match as any).associate_summary}
                                    </Typography>
                                )}
                                
                                {/* Previous Work with IOD PARC */}
                                {(match as any).has_worked_with_us && (match as any).allocation_summary && (match as any).allocation_summary.length > 0 && (
                                    <Accordion 
                                        sx={{ 
                                            mb: 2, 
                                            bgcolor: 'rgba(76, 175, 80, 0.08)', 
                                            border: '1px solid',
                                            borderColor: 'success.light',
                                            '&:before': { display: 'none' }
                                        }}
                                    >
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <WorkHistoryIcon color="success" fontSize="small" />
                                                <Typography variant="subtitle2" color="success.main" sx={{ fontWeight: 600 }}>
                                                    Previous Work with IOD PARC ({(match as any).allocation_count} project{(match as any).allocation_count !== 1 ? 's' : ''})
                                                </Typography>
                                            </Stack>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                                {(match as any).allocation_summary.map((alloc: any, idx: number) => (
                                                    <Box component="li" key={idx} sx={{ mb: 1 }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {alloc.project_client || 'Client N/A'}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {alloc.project_name || alloc.project_number} — {alloc.role || 'Role N/A'}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                                {(match as any).allocation_count > 5 && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                        + {(match as any).allocation_count - 5} more projects
                                                    </Typography>
                                                )}
                                            </Box>
                                        </AccordionDetails>
                                    </Accordion>
                                )}
                                
                                {/* Evidence-Based Matching */}
                                <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(0, 0, 0, 0.2)', borderRadius: 1 }}>
                                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                                        Match Evidence
                                    </Typography>
                                        {/* Check if there's ANY evidence at all - 10 aligned categories */}
                                        {(() => {
                                            const filteredCategories = getFilteredEvidenceCategories(selectedCategories);
                                            const hasAnyEvidence = filteredCategories.some(({ field }) => {
                                                const evidence = (match as any)[field];
                                                return evidence && evidence.length > 0;
                                            });
                                            
                                            if (!hasAnyEvidence) {
                                                return (
                                                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary', p: 1 }}>
                                                        No specific evidence found for the searched criteria. This CV was matched based on semantic similarity to your requirements.
                                                    </Typography>
                                                );
                                            }
                                            return null;
                                        })()}
                                        
                                        {/* Render filtered evidence categories */}
                                        {getFilteredEvidenceCategories(selectedCategories).map(({ title, field }) => {
                                            const evidence = (match as any)[field];
                                            if (!evidence || evidence.length === 0) return null;
                                            
                                            return (
                                                <Box key={field} sx={{ mb: 2 }}>
                                                    <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'success.main' }}>
                                                        {title}
                                                    </Typography>
                                                    <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2 }}>
                                                        {evidence.map((evidenceItem: any, idx: number) => {
                                                            let text = '';
                                                            if (typeof evidenceItem === 'string') {
                                                                text = evidenceItem;
                                                            } else if (evidenceItem?.country && evidenceItem?.evidence) {
                                                                text = `${evidenceItem.country}: ${evidenceItem.evidence}`;
                                                            } else if (evidenceItem?.country) {
                                                                text = evidenceItem.country;
                                                            } else if (evidenceItem?.degree) {
                                                                text = `${evidenceItem.degree}${evidenceItem.institution ? `, ${evidenceItem.institution}` : ''}${evidenceItem.year ? ` (${evidenceItem.year})` : ''}`;
                                                            } else if (evidenceItem?.method && evidenceItem?.evidence) {
                                                                text = `${evidenceItem.method}: ${evidenceItem.evidence}`;
                                                            } else if (evidenceItem?.method) {
                                                                text = evidenceItem.method;
                                                            } else if (evidenceItem?.sector && evidenceItem?.evidence) {
                                                                text = `${evidenceItem.sector}: ${evidenceItem.evidence}`;
                                                            } else if (evidenceItem?.sector) {
                                                                text = evidenceItem.sector;
                                                            } else if (evidenceItem?.language) {
                                                                text = `${evidenceItem.language}${evidenceItem.proficiency ? `: ${evidenceItem.proficiency}` : ''}`;
                                                            } else {
                                                                text = JSON.stringify(evidenceItem);
                                                            }
                                                            return renderEvidenceItem(text, idx, title);
                                                        })}
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                </Box>
                                
                                {/* Viability Summary */}
                                {(match as any).viability_summary && (
                                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary', mt: 2, p: 1.5, bgcolor: 'rgba(0, 0, 0, 0.05)', borderRadius: 1, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                                        {(match as any).viability_summary}
                                    </Typography>
                                )}
                                
                                {/* Key Strengths & Experience - Collapsible */}
                                {match.key_qualifications.length > 0 && (
                                    <Accordion sx={{ mt: 2, boxShadow: 'none', '&:before': { display: 'none' } }}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Typography variant="subtitle2">
                                                Key Strengths & Experience ({match.key_qualifications.length})
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Stack spacing={0.5}>
                                                {match.key_qualifications.map((qual, i) => {
                                                    // Handle both string and object formats from LLM
                                                    const qualObj = qual as any;
                                                    let qualText = '';
                                                    
                                                    if (typeof qual === 'string') {
                                                        qualText = qual;
                                                    } else if (qualObj?.qualification) {
                                                        // Format: "PhD in Economics (Oxford, 2015)" or "10 years M&E"
                                                        qualText = qualObj.qualification;
                                                        if (qualObj.institution || qualObj.year_completed) {
                                                            const details = [qualObj.institution, qualObj.year_completed].filter(Boolean).join(', ');
                                                            qualText += ` (${details})`;
                                                        } else if (qualObj.years_experience) {
                                                            qualText += ` (${qualObj.years_experience} years)`;
                                                        }
                                                    } else {
                                                        qualText = String(qual);
                                                    }
                                                    
                                                    return (
                                                        <Typography key={i} variant="body2" sx={{ display: 'flex', gap: 1 }}>
                                                            <Box component="span" sx={{ color: 'primary.main' }}>•</Box>
                                                            {qualText}
                                                        </Typography>
                                                    );
                                                })}
                                            </Stack>
                                        </AccordionDetails>
                                    </Accordion>
                                )}
                                
                                {/* Free-Text Keyword Evidence - Collapsible */}
                                {match.freetext_evidence && match.freetext_evidence.totalHits > 0 && (
                                    <Accordion sx={{ mt: 2, boxShadow: 'none', bgcolor: 'rgba(25, 118, 210, 0.05)', '&:before': { display: 'none' } }}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <SearchIcon color="info" fontSize="small" />
                                                <Typography variant="subtitle2" color="info.main" sx={{ fontWeight: 600 }}>
                                                    Free-Text Keywords ({match.freetext_evidence.totalHits} hits, +{((match.freetext_boost || 0) * 100).toFixed(0)}% boost)
                                                </Typography>
                                            </Stack>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Stack spacing={2}>
                                                {match.freetext_evidence.terms.map((termEvidence, termIdx) => (
                                                    <Box key={termIdx}>
                                                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'info.main', mb: 0.5 }}>
                                                            "{termEvidence.term}" — {termEvidence.hitCount} occurrence{termEvidence.hitCount !== 1 ? 's' : ''}
                                                        </Typography>
                                                        {termEvidence.snippets.length > 0 && (
                                                            <Box sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'info.light' }}>
                                                                {termEvidence.snippets.slice(0, 5).map((snippet, snippetIdx) => (
                                                                    <Typography
                                                                        key={snippetIdx}
                                                                        variant="caption"
                                                                        component="div"
                                                                        sx={{
                                                                            color: 'text.secondary',
                                                                            mb: 0.5,
                                                                            '& b': { color: 'info.main', fontWeight: 600 }
                                                                        }}
                                                                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(snippet.context) }}
                                                                    />
                                                                ))}
                                                                {termEvidence.snippets.length > 5 && (
                                                                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                                        + {termEvidence.snippets.length - 5} more snippets
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </AccordionDetails>
                                    </Accordion>
                                )}
                                
                                {/* Action Buttons */}
                                <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                                    <Button
                                        variant="contained"
                                        startIcon={<ArticleIcon />}
                                        onClick={() => handleViewDocument(match)}
                                        sx={{ flex: 1 }}
                                    >
                                        View CV
                                    </Button>
{/* Chat with CV - Temporarily disabled, may re-enable later
                                    <Button
                                        variant="outlined"
                                        startIcon={<ChatIcon />}
                                        onClick={() => handleChatWithDocument(match)}
                                        sx={{ flex: 1 }}
                                    >
                                        Chat with CV
                                    </Button>
                                    */}
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
            
            {/* Additional Verified CVs (passed verification but no LLM analysis) */}
            {additionalMatches && additionalMatches.length > 0 && (
                <Box sx={{ mt: 4 }}>
                    <Typography variant="h6" gutterBottom sx={{ mb: 1 }}>
                        Additional Verified CVs ({additionalMatches.length})
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                        These CVs also passed all required criteria verification but were not analyzed in detail. 
                        Click to view the full CV.
                    </Typography>
                    
                    <Grid container spacing={2}>
                        {additionalMatches.map((match, index) => (
                            <Grid item xs={12} key={match.cv_id}>
                                <Card elevation={2} sx={{ 
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: 'background.paper'
                                }}>
                                    <CardContent sx={{ py: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                    #{matches.length + index + 1}: {match.associate_name || match.filename}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {extractCountry(match.folder_name)}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ textAlign: 'right', mx: 3 }}>
                                                <Typography variant="h6" color="text.secondary">
                                                    {Math.round(match.similarity_score * 100)}%
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Similarity
                                                </Typography>
                                            </Box>
                                            <Button
                                                variant="contained"
                                                startIcon={<ArticleIcon />}
                                                onClick={() => {
                                                    const documentPath = match.source_location || 
                                                        (match.folder_name ? `${match.folder_name}/${match.filename}` : match.filename);
                                                    const cleanPath = documentPath.replace(/\\/g, '/');
                                                    const encodedPath = encodeURIComponent(cleanPath);
                                                    window.open(`#/document/cv/${encodedPath}`, '_blank');
                                                }}
                                            >
                                                View CV
                                            </Button>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}
        </Box>
    );
};

export default CVMatchingResults;
